import { TitleCasePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TextInputModel } from 'projects/money-movement-ui/src/app/shared/components/controls/text-input-control/TextInputModel';
import { NamePipe } from 'projects/money-movement-ui/src/app/shared/pipes/NamePipe';
import { Constants, thirdPartyCheckFieldsErrorMap } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { Observable, Subject, filter, of, takeUntil, withLatestFrom } from 'rxjs';
import * as CustomValidators from '../../../../shared/utilities/FormValidators';
import { zipCodeAllowedChars } from '../../../../shared/utilities/regex-patterns';
import { FirmDetails } from '../../../models/check/CheckData';
import { createControl } from '../../../utilities/formUtilities';
import { CheckTransactionDetailsForm, RecipientValues } from '../../../models/form/TransactionFormGroup';

@Component({
  standalone: false,
  selector: 'mm-check-third-party-address-fields',
  templateUrl: './check-third-party-address-fields.component.html',
  styleUrls: ['./check-third-party-address-fields.component.scss']
})
export class CheckThirdPartyAddressFieldsComponent implements OnInit {
  @Input() parentForm!: FormGroup<CheckTransactionDetailsForm>;
  @Input() firmAddress: FirmDetails;
  @Input() recipientSelection$: Observable<string>;
  @Input() isFromEdit$: Observable<boolean> = of(false);
  @Input() isFromCreateStandingAuth: boolean = false;
  @Input() shouldDisableFieldForEditQcd: boolean = false;
  zipCodeAllowedChars = zipCodeAllowedChars;
  createSaThirdAddresseeNameInfoText = Constants.createSaThirdAddresseeNameInfoText;
  readonly controlNames = {
    addresseeName: 'addresseeName',
    addresseeNameCont: 'addresseeNameCont',
    addresseeNameCont2: 'addresseeNameCont2',
    streetAddress: 'streetAddress',
    streetAddressCont: 'streetAddressCont'
  }
  addresseeNameModel: TextInputModel = {
    controlName: this.controlNames.addresseeName,
    label: 'Addressee name',
    hasGreyText: true,
    greyText: '(person or business)',
    htmlMaxLength: 50,
    hasIconTooltip: false,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: 'TBD'
    }
  };
  addresseeNameContModel: TextInputModel = {
    controlName: this.controlNames.addresseeNameCont,
    label: 'Addressee name continued',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 50
  };
  addresseeNameCont2Model: TextInputModel = {
    controlName: this.controlNames.addresseeNameCont2,
    label: 'Addressee name continued',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 50
  };
  streetAddressModel: TextInputModel = {
    controlName: this.controlNames.streetAddress,
    label: 'Street address',
    hasGreyText: false,
    htmlMaxLength: 45
  };
  streetAddressContModel: TextInputModel = {
    controlName: this.controlNames.streetAddressCont,
    label: 'Address continued',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 45
  };
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private namePipe: NamePipe, private titleCasePipe: TitleCasePipe) { }

  ngOnInit(): void {
    createControl(this.parentForm, this.controlNames.addresseeName, '', [Validators.required, CustomValidators.isEmptyString], undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.addresseeNameCont, '',undefined, undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.addresseeNameCont2, '',undefined, undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.streetAddress, '', [Validators.required, CustomValidators.isEmptyString], undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.streetAddressCont, '',undefined, undefined, this.shouldDisableFieldForEditQcd);

    // When recipient type is advisoryFirm and NOT edit flow, set the form values to the firm address
    this.recipientSelection$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.isFromEdit$),
      filter(([recipientType, isFromEdit]) => recipientType === 'advisoryFirm' && !isFromEdit)
    ).subscribe(() => {
      this.setFirmAddressFields(this.firmAddress);
    })

    // When recipient selection changes, reset all fields except for when recipient is advisoryFirm
    this.recipient?.valueChanges.pipe(
      takeUntil(this.destroy$),
      filter((recipientType) => recipientType !== 'advisoryFirm')
    ).subscribe(() => {
      Object.keys(this.controlNames).forEach((name) => {
        const names = this.controlNames;
        this.parentForm?.get(this.controlNames[name as keyof typeof names])?.reset('');
      });
    })
  }

  ngOnDestroy() {
    if (this.addresseeName) this.parentForm.removeControl('addresseeName');
    if (this.addresseeNameCont) this.parentForm.removeControl('addresseeNameCont');
    if (this.addresseeNameCont2) this.parentForm.removeControl('addresseeNameCont2');
    if (this.streetAddress) this.parentForm.removeControl('streetAddress');
    if (this.streetAddressCont) this.parentForm.removeControl('streetAddressCont');

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get addresseeName(): FormControl<string> {
    return this.parentForm.get('addresseeName') as FormControl<string>;
  }
  get addresseeNameCont(): FormControl<string> {
    return this.parentForm.get('addresseeNameCont') as FormControl<string>;
  }
  get addresseeNameCont2(): FormControl<string> {
    return this.parentForm.get('addresseeNameCont2') as FormControl<string>;
  }
  get streetAddress(): FormControl<string> {
    return this.parentForm.get('streetAddress') as FormControl<string>;
  }
  get streetAddressCont(): FormControl<string> {
    return this.parentForm.get('streetAddressCont') as FormControl<string>;
  }
  get addresseeNameError(): string {
    const error = this.mapErrors(this.addresseeNameModel.label, this.addresseeName);
    return error === undefined ? '' : error;
  }
  get streetAddressError(): string {
    const error = this.mapErrors(this.streetAddressModel.label, this.streetAddress);
    return error === undefined ? '' : error;
  }
  get recipient(): FormControl<typeof RecipientValues[number]> {
    return this.parentForm.get('recipient') as FormControl<typeof RecipientValues[number]>;
  }
  get showThirdAddresseeInfoBanner(): boolean {
    return this.isFromCreateStandingAuth && (this.addresseeNameCont2.value != '') && !this.shouldDisableFieldForEditQcd;
  }
  get addresseeNameCont2Class(): string {
    return this.showThirdAddresseeInfoBanner ? 'small-bottom-spacer' : 'big-bottom-spacer';
  }

  private mapErrors(
    name: string,
    control: FormControl | undefined
  ): string | undefined {
    const errorMap = thirdPartyCheckFieldsErrorMap(name);
    const errorKeys = Object.keys(control?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof thirdPartyCheckFieldsErrorMap];
    }

    return '';
  }

  private setFirmAddressFields(firmDetails: FirmDetails): void {
    this.addresseeName.setValue(this.namePipe.transform(firmDetails?.name ?? ''));
    this.addresseeNameCont2.setValue('');
    this.addresseeNameCont.setValue('');
    this.streetAddress.setValue(this.titleCasePipe.transform(firmDetails?.streetAddr1 ?? ''));
    this.streetAddressCont.setValue(this.titleCasePipe.transform(firmDetails?.streetAddr2 ?? ''));
  }
}
